import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { LINKS, PHOTOS } from "@/data/content";

const FEATURES = [
  "63+ spirit-led rooms — with new ones arriving every month",
  "Grief & trauma support: the Grief Continuum, EFT Tapping, Sacred Rescue",
  "Daily rituals — guided meditation, breathwork & affirmations",
  "Live events, a prayer garden & a global healing community",
  "Private journals, vision boards & soul tools — entirely yours",
];

export default function InfiniteApp() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section id="app" ref={ref} data-testid="app-section" className="relative overflow-hidden bg-[#F7F1E6] py-28 lg:py-40">
      <motion.div
        className="pointer-events-none absolute -left-40 top-1/3 h-[30rem] w-[30rem] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(197,160,89,0.16), transparent 65%)" }}
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        aria-hidden="true"
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-20 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="font-body text-[0.65rem] font-medium uppercase tracking-[0.5em] text-[#C5A059]"
          >
            Chapter 07 — My Infinite App
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.76, 0, 0.24, 1] }}
            className="mt-6 font-display text-4xl font-light leading-tight tracking-tighter text-[#6B5D4F] sm:text-5xl lg:text-6xl"
          >
            Healing, <span className="italic text-[#C5A059]">In Your Pocket</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mt-8 max-w-xl font-body text-base font-light leading-relaxed text-[#8B7D6B]"
          >
            Susan's digital sanctuary — 63+ spirit-led rooms, with new ones arriving every month.
            A daily companion for grief support, guided ritual, community, and personal growth,
            built from three decades of Susan's healing work.
          </motion.p>

          <div className="mt-10 max-w-xl border-t border-[#6B5D4F]/10">
            {FEATURES.map((label, i) => (
              <motion.div
                key={label}
                data-testid={`app-feature-${i}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.07 }}
                className="group flex items-baseline gap-5 border-b border-[#6B5D4F]/10 py-5"
              >
                <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-[#C5A059]" aria-hidden="true" />
                <span className="font-body text-base font-light text-[#6B5D4F] transition-transform duration-500 group-hover:translate-x-2">
                  {label}
                </span>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-8 flex max-w-xl flex-wrap items-center gap-x-5 gap-y-2 font-body text-[0.6rem] font-medium uppercase tracking-[0.3em] text-[#8B7D6B]"
          >
            <span>Healing & Grief</span>
            <span className="h-1 w-1 rotate-45 bg-[#C5A059]" aria-hidden="true" />
            <span>Spirit & Guidance</span>
            <span className="h-1 w-1 rotate-45 bg-[#C5A059]" aria-hidden="true" />
            <span>Play & Create</span>
            <span className="h-1 w-1 rotate-45 bg-[#C5A059]" aria-hidden="true" />
            <span>Ritual & Community</span>
            <span className="h-1 w-1 rotate-45 bg-[#C5A059]" aria-hidden="true" />
            <span>Growth & Your Story</span>
          </motion.p>

          <motion.a
            data-testid="app-visit-link"
            href={LINKS.app}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="group mt-10 flex w-fit items-center gap-3 bg-[#6B5D4F] px-8 py-4 font-body text-[0.65rem] font-medium uppercase tracking-[0.3em] text-[#FDFAF4] transition-colors duration-500 hover:bg-[#C5A059]"
          >
            Enter My Infinite App
            <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </motion.a>
        </div>

        <div className="flex justify-center lg:col-span-5">
          <motion.div
            data-testid="app-photo-frame"
            style={{ y: phoneY }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="relative"
          >
            <div className="relative w-72 md:w-80">
              <div className="absolute -left-4 -top-4 h-full w-full border border-[#C5A059]/50" aria-hidden="true" />
              <img
                src={PHOTOS.holdingBook}
                alt="Susan Grau holding Infinite Life, Infinite Lessons"
                className="relative aspect-[3/4] w-full object-cover object-[center_72%] shadow-[0_30px_60px_-20px_rgba(107,93,79,0.25)]"
              />
            </div>
            <p className="mt-8 text-center font-script text-3xl text-[#C5A059]">infinite wisdom, daily</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
